import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    CircularProgress,
    Button,
    IconButton,
    Divider,
    Box,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import studentApi from '../StudentApi';
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';

const PracticeSetsModal = ({ open, onClose, examId, subjectId, topicId }) => {
    const [practiceSets, setPracticeSets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;

        const fetchPracticeSets = async () => {
            setLoading(true);
            try {
                const res = await studentApi.get('/practice-sets');

                 // ✅ log here to see raw API data
            console.log("API data:", res.data.data);

                const filtered = res.data.data.filter(
                    set =>
                        set.exam_id === parseInt(examId) &&
                        (subjectId ? set.subject_id === parseInt(subjectId) : true) &&
                        (topicId ? set.topic_id === parseInt(topicId) : true)
                );

                // ✅ log here to see filtered sets
            console.log("Filtered practice sets:", filtered);
                setPracticeSets(filtered);
            } catch (err) {
                console.error(err);
                setError('Failed to fetch practice sets');
            } finally {
                setLoading(false);
            }
        };

        fetchPracticeSets();
    }, [open, examId, subjectId, topicId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    width: isMobile ? '95%' : '80%',
                    m: isMobile ? 1 : 3,
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontWeight: 'bold',
                    fontSize: isMobile ? '1rem' : '1.4rem',
                    pr: 5,
                    textAlign: 'center',
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    position: 'relative',
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    Available Practice Sets
                </motion.div>

                <motion.div
                    whileHover={{ scale: 1.2, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ color: '#f44336', transition: 'all 0.3s ease' }}
                    >
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </motion.div>
            </DialogTitle>

            <DialogContent>
                {loading && <CircularProgress />}
                {error && <p style={{ color: 'red' }}>{error}</p>}

                {!loading && !error && practiceSets.length === 0 && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                    >
                        No practice sets available.
                    </Typography>
                )}

                {!loading && practiceSets.length > 0 && (
                    <List>
                        {practiceSets.map((set, index) => (
                            <React.Fragment key={set.id}>
                                <ListItem
                                    sx={{
                                        mb: 1,
                                        p: isMobile ? 1.5 : 2,
                                        borderRadius: 2,
                                        backgroundColor:
                                            index % 2 === 0
                                                ? 'rgba(0, 123, 255, 0.05)'
                                                : 'rgba(0,0,0,0.03)',
                                        '&:hover': { backgroundColor: 'rgba(0, 123, 255, 0.12)' },
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        alignItems: isMobile ? 'flex-start' : 'center',
                                        justifyContent: 'space-between',
                                        gap: isMobile ? 1.5 : 0,
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" flex={1}>
                                        <Box display="flex" alignItems="center" mb={0.5}>
                                            <AssignmentIcon sx={{ color: 'primary.main', mr: 1 }} fontSize={isMobile ? 'small' : 'medium'} />
                                            <Typography fontWeight="bold" fontSize={isMobile ? '0.9rem' : '1rem'}>
                                                {set.name}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" flexWrap="wrap" alignItems="center" gap={isMobile ? 1 : 2}>
                                            <Box display="flex" alignItems="center">
                                                <QuizIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                                                <Typography variant="body2" color="text.secondary" fontSize={isMobile ? '0.75rem' : '0.85rem'}>
                                                    {set.total_questions} Questions
                                                </Typography>
                                            </Box>

                                            <Box display="flex" alignItems="center">
                                                <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                                                <Typography variant="body2" color="text.secondary" fontSize={isMobile ? '0.75rem' : '0.85rem'}>
                                                    {set.duration_minutes} mins
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size={isMobile ? 'small' : 'medium'}
                                        endIcon={!isMobile && <PlayArrowIcon />}
                                        sx={{ ml: isMobile ? 0 : 2, borderRadius: 2, alignSelf: isMobile ? 'flex-end' : 'center' }}
                                        onClick={() => {
                                            console.log("Navigating to practice set ID:", set.id);
                                            navigate(`/test-practice/${set.id}`);
                                            onClose();
                                        }}
                                    >
                                        {isMobile ? <PlayArrowIcon /> : 'Start'}
                                    </Button>
                                </ListItem>

                                {index < practiceSets.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PracticeSetsModal;
